import React, { useState, useCallback, useEffect, useRef } from "react";
import { rootApi } from '../api/api';
import styles from "../styles/PlaceOfBirthField.module.css";

// Google Places bills autocomplete keystrokes + the final pick as ONE session when they share a
// token. A session ends when the user picks a place; an idle token is replaced after 3 minutes.
const SESSION_IDLE_MS = 3 * 60 * 1000;

const newSessionToken = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

/**
 * PlaceOfBirthField Component
 * 
 * AutoSuggest input field with API-based search for place of birth selection
 * Features:
 * - API search with 3-character minimum trigger
 * - 300ms debounced search
 * - Keyboard navigation (Arrow Up/Down, Enter)
 * - Validation against selected location
 * - Loading indicator
 * - Request cancellation on new searches
 */
const PlaceOfBirthField = ({ 
  value, 
  onChange, 
  placeholder = "Search for place, district, or state...", 
  onValidationError,
  required = false 
}) => {
  const [show, setShow] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const [validationError, setValidationError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [userInteracted, setUserInteracted] = useState(false); // Track if user has typed
  const [resolving, setResolving] = useState(false); // Fetching details of a picked Google suggestion
  
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);
  const sessionRef = useRef(null); // { token, lastUsed } for the current Google session
  const resolveSeqRef = useRef(0); // Lets a newer keystroke/selection discard a stale resolve
  const resolvingRef = useRef(false); // Same as `resolving`, readable from the blur timeout

  const getSessionToken = useCallback(() => {
    const now = Date.now();
    const session = sessionRef.current;
    if (!session || now - session.lastUsed > SESSION_IDLE_MS) {
      sessionRef.current = { token: newSessionToken(), lastUsed: now };
    } else {
      session.lastUsed = now;
    }
    return sessionRef.current.token;
  }, []);

  // API search function
  const searchLocations = useCallback(async (searchTerm) => {
    // Trim before the length check so it matches the backend's trimmed validation.
    // Without this, a trailing space ("ab ") passes here but fails server-side with a 400.
    const trimmedTerm = (searchTerm || '').trim();
    if (trimmedTerm.length < 3) {
      setFiltered([]);
      setShow(false);
      return;
    }

    // Cancel previous request if still running
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const { data } = await rootApi.get('/search', {
        params: {
          q: trimmedTerm,
          limit: 20,
          session_token: getSessionToken(),
        },
        signal: abortControllerRef.current.signal
      });
      // Keep DB rows (search_key) and Google suggestions (place_id, resolved on select)
      const validLocations = (data.data || []).filter(loc => loc && (loc.search_key || loc.place_id));
      setFiltered(validLocations);
      setShow(true);
      setHighlight(-1);
    } catch (error) {
      if (error.name === 'CanceledError') {
        // Request was cancelled, do nothing
        return;
      }
      console.error('Location search error:', error);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  }, [getSessionToken]);

  // Debounced search effect
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Skip search if a location is already selected and value matches
    if (selectedLocation && selectedLocation.search_key === value) {
      return;
    }

    debounceRef.current = setTimeout(() => {
      searchLocations(value);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value, searchLocations, selectedLocation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Validate when suggestions dropdown closes without selection
  useEffect(() => {
    // Only validate if user has interacted (prevents errors on pre-filled values)
    if (!userInteracted) return;
    
    if (!show && value && value.trim().length >= 3 && !selectedLocation && !loading && !resolving) {
      // User typed enough for suggestions but didn't select anything
      const errorMsg = "Please select a place of birth from the suggestions";
      setValidationError(errorMsg);
      if (onValidationError) onValidationError(errorMsg);
    } else if (!show && (!value || !value.trim()) && !required) {
      // Field is optional and empty - clear any errors
      setValidationError("");
      if (onValidationError) onValidationError("");
    }
  }, [show, value, selectedLocation, loading, resolving, required, userInteracted]);

  // Validate if user selected from dropdown
  const validatePlace = (inputValue) => {
    // Check if empty
    if (!inputValue.trim()) {
      // If required, show error
      if (required) {
        const errorMsg = "Place of birth is required";
        setValidationError(errorMsg);
        if (onValidationError) onValidationError(errorMsg);
        return false;
      }
      // If not required, clear error
      setValidationError("");
      if (onValidationError) onValidationError("");
      return true;
    }

    // Valid only if user selected from dropdown (selectedLocation exists)
    if (!selectedLocation) {
      const errorMsg = "Please select a place of birth from the suggestions";
      setValidationError(errorMsg);
      if (onValidationError) onValidationError(errorMsg);
      return false;
    } else {
      setValidationError("");
      if (onValidationError) onValidationError("");
      return true;
    }
  };

  const handleInput = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);

    // A pending resolve belongs to the old text — discard its result
    resolveSeqRef.current += 1;
    resolvingRef.current = false;
    setResolving(false);
    setShow(true);
    setHighlight(-1);
    
    // Mark that user has interacted with the field
    if (!userInteracted) {
      setUserInteracted(true);
    }
    
    // Clear selected location when user types (no longer valid until they select again)
    if (selectedLocation) {
      setSelectedLocation(null);
    }
    
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError("");
      if (onValidationError) onValidationError("");
    }
  };

  const finishSelection = (location) => {
    // Store raw search_key (without spaces) for backend lookup
    onChange(location.search_key);

    // Mark location as selected for validation
    setSelectedLocation(location);

    setShow(false);
    setHighlight(-1);

    // Clear any validation error
    setValidationError("");
    if (onValidationError) onValidationError("");
  };

  const handleSelect = async (location) => {
    if (!location) return;

    // Place already in our DB: it carries its search_key
    if (location.search_key) {
      sessionRef.current = null; // picking a place ends the Google session
      finishSelection(location);
      return;
    }

    // Google suggestion: the backend fetches + stores it and returns the search_key to use
    if (!location.place_id) {
      console.error('Invalid location selected:', location);
      return;
    }

    const seq = ++resolveSeqRef.current;
    resolvingRef.current = true;
    setResolving(true);
    try {
      const { data } = await rootApi.post('/search/resolve', {
        place_id: location.place_id,
        session_token: getSessionToken(),
      });
      if (seq !== resolveSeqRef.current) return; // user typed or picked again meanwhile

      const resolved = data?.data;
      if (!resolved?.search_key) throw new Error('Resolve response had no search_key');

      sessionRef.current = null;
      finishSelection(resolved);
    } catch (error) {
      if (seq !== resolveSeqRef.current) return;
      console.error('Place resolve error:', error);
      const errorMsg = "Could not load this place. Please try another.";
      setValidationError(errorMsg);
      if (onValidationError) onValidationError(errorMsg);
    } finally {
      if (seq === resolveSeqRef.current) {
        resolvingRef.current = false;
        setResolving(false);
      }
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShow(false);
      // Only validate on blur if user has interacted (prevents errors on pre-filled values)
      if (userInteracted && !resolvingRef.current) {
        validatePlace(value);
      }
    }, 150);
  };

  const handleKeyDown = (e) => {
    if (!show || filtered.length === 0) return;
    
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      handleSelect(filtered[highlight]);
    } else if (e.key === "Escape") {
      setShow(false);
    }
  };

  return (
    <div className={`${styles.container} ${validationError ? styles.hasError : ''}`}>
      <div className={styles.inputWrapper}>
        <input
          ref={inputRef}
          type="text"
          value={selectedLocation && value === selectedLocation.search_key 
            ? value.replace(/,/g, ', ') 
            : (value || '')
          }
          onChange={handleInput}
          onFocus={() => {
            if (value && value.length >= 3 && filtered.length > 0) {
              setShow(true);
            }
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          required={required}
          className={`${styles.input} ${validationError ? styles.inputError : ''}`}
          aria-label="Place of Birth"
          aria-required={required}
          aria-invalid={!!validationError}
          aria-describedby={validationError ? "place-error" : undefined}
        />
        
        {(loading || resolving) && (
          <div className={styles.loadingSpinner}>
            <div className={styles.spinner} />
          </div>
        )}
      </div>
      
      {validationError && (
        <div 
          id="place-error"
          className={styles.errorMessage}
          role="alert"
          aria-live="polite"
        >
          {validationError}
        </div>
      )}
      
      {show && filtered.length > 0 && (
        <ul className={styles.suggestionsList}>
          {filtered.map((location, i) => {
            // Safety check and format search_key with spaces for display
            const displayText = location?.search_key 
              ? location.search_key.replace(/,/g, ', ') 
              : (location?.description || 'Unknown Location');
            
            return (
              <li
                key={location.id || i}
                className={`${styles.suggestionItem} ${i === highlight ? styles.highlighted : ''}`}
                onMouseDown={() => handleSelect(location)}
                onMouseEnter={() => setHighlight(i)}
              >
                {displayText}
              </li>
            );
          })}
        </ul>
      )}
      
      {show && value && value.length >= 3 && filtered.length === 0 && !loading && (
        <div className={styles.noResults}>
          No locations found
        </div>
      )}
    </div>
  );
};

export default PlaceOfBirthField;
